'use client';

import React, { useState, useCallback } from 'react';
import { Upload, X, Check, AlertCircle, Loader2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Stock } from '@/types';
import { fetchStockData, searchStocks, findPriceForDate, fetchHistoricalPrice } from '@/lib/stockService';

const generateId = () => Math.random().toString(36).substr(2, 9);

const CANADIAN_EXCHANGES = ['TOR', 'TSX', 'VAN', 'CNQ'];

interface PurchaseDetails {
  shares: string;
  price: string;
  currency: 'USD' | 'CAD';
  purchasedDate: string;
  isPriceFetching?: boolean;
  priceError?: string;
}

interface BulkStockEntry {
  id: string;
  symbol: string;
  resolvedSymbol?: string;
  name?: string;
  exchange?: string;
  status: 'pending' | 'resolving' | 'resolved' | 'adding' | 'added' | 'error' | 'duplicate' | 'skipped';
  error?: string;
  purchaseDetails?: PurchaseDetails;
  isExpanded?: boolean;
}

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingSymbols: string[];
  onStocksAdded: (stocks: Stock[]) => void;
  tier: string;
  portfolioCount: number;
  assetLimit: number;
}

export const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  existingSymbols,
  onStocksAdded,
  tier,
  portfolioCount,
  assetLimit,
}) => {
  const [rawInput, setRawInput] = useState('');
  const [entries, setEntries] = useState<BulkStockEntry[]>([]);
  const [step, setStep] = useState<'input' | 'review' | 'adding'>('input');
  const [isResolving, setIsResolving] = useState(false);
  const [addingProgress, setAddingProgress] = useState({ current: 0, total: 0 });
  const [addSymbolInput, setAddSymbolInput] = useState('');
  const [isAddingSymbol, setIsAddingSymbol] = useState(false);

  const reset = useCallback(() => {
    setRawInput('');
    setEntries([]);
    setStep('input');
    setIsResolving(false);
    setAddingProgress({ current: 0, total: 0 });
    setAddSymbolInput('');
    setIsAddingSymbol(false);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const parseSymbols = (input: string): string[] => {
    return input
      .split(/[\n,;\s]+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => s.length > 0 && s.length <= 10)
      .filter((s, i, arr) => arr.indexOf(s) === i);
  };

  const handleResolve = async () => {
    const symbols = parseSymbols(rawInput);
    if (symbols.length === 0) return;

    const initialEntries: BulkStockEntry[] = symbols.map(symbol => {
      const isDuplicate = existingSymbols.includes(symbol);
      return {
        id: generateId(),
        symbol,
        status: isDuplicate ? 'duplicate' : 'pending',
        error: isDuplicate ? 'Already in portfolio' : undefined,
      };
    });

    setEntries(initialEntries);
    setStep('review');
    setIsResolving(true);

    const toResolve = initialEntries.filter(e => e.status === 'pending');

    for (let i = 0; i < toResolve.length; i += 3) {
      const batch = toResolve.slice(i, i + 3);
      await Promise.all(
        batch.map(async (entry) => {
          setEntries(prev =>
            prev.map(e => e.id === entry.id ? { ...e, status: 'resolving' } : e)
          );

          try {
            const results = await searchStocks(entry.symbol);
            const exact = results.find(r => r.symbol.toUpperCase() === entry.symbol);
            const best = exact || results[0];

            if (best) {
              setEntries(prev =>
                prev.map(e =>
                  e.id === entry.id
                    ? {
                        ...e,
                        status: 'resolved',
                        resolvedSymbol: best.symbol,
                        name: best.name,
                        exchange: best.exchange,
                      }
                    : e
                )
              );
            } else {
              setEntries(prev =>
                prev.map(e =>
                  e.id === entry.id
                    ? { ...e, status: 'error', error: 'Symbol not found' }
                    : e
                )
              );
            }
          } catch {
            setEntries(prev =>
              prev.map(e =>
                e.id === entry.id
                  ? { ...e, status: 'error', error: 'Lookup failed' }
                  : e
              )
            );
          }
        })
      );
    }

    setIsResolving(false);
  };

  const handleRemoveEntry = (id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleTogglePurchaseDetails = (id: string) => {
    setEntries(prev =>
      prev.map(e => {
        if (e.id !== id) return e;
        if (e.isExpanded) {
          // Collapse: clear purchase details
          return { ...e, isExpanded: false, purchaseDetails: undefined };
        }
        // Expand: initialize with defaults
        const isCanadian = CANADIAN_EXCHANGES.some(ex =>
          (e.exchange || '').toUpperCase().includes(ex)
        );
        return {
          ...e,
          isExpanded: true,
          purchaseDetails: {
            shares: '',
            price: '',
            currency: isCanadian ? 'CAD' : 'USD',
            purchasedDate: '',
          },
        };
      })
    );
  };

  const handlePurchaseDetailChange = (id: string, field: keyof PurchaseDetails, value: string) => {
    setEntries(prev =>
      prev.map(e => {
        if (e.id !== id || !e.purchaseDetails) return e;
        return {
          ...e,
          purchaseDetails: { ...e.purchaseDetails, [field]: value, ...(field === 'purchasedDate' ? { priceError: undefined } : {}) },
        };
      })
    );

    // Auto-fill price when purchase date changes
    if (field === 'purchasedDate' && value) {
      const entry = entries.find(e => e.id === id);
      if (entry) {
        handlePriceLookup(id, entry.resolvedSymbol || entry.symbol, value);
      }
    }
  };

  const handlePriceLookup = async (id: string, symbol: string, date: string) => {
    setEntries(prev =>
      prev.map(e => {
        if (e.id !== id || !e.purchaseDetails) return e;
        return { ...e, purchaseDetails: { ...e.purchaseDetails, isPriceFetching: true, priceError: undefined } };
      })
    );

    try {
      const price = await fetchHistoricalPrice(symbol, date);
      if (price !== null && price !== undefined) {
        setEntries(prev =>
          prev.map(e => {
            if (e.id !== id || !e.purchaseDetails) return e;
            return { ...e, purchaseDetails: { ...e.purchaseDetails, price: String(price), isPriceFetching: false } };
          })
        );
      } else {
        setEntries(prev =>
          prev.map(e => {
            if (e.id !== id || !e.purchaseDetails) return e;
            return { ...e, purchaseDetails: { ...e.purchaseDetails, isPriceFetching: false, priceError: 'Price not found for this date — enter manually' } };
          })
        );
      }
    } catch {
      setEntries(prev =>
        prev.map(e => {
          if (e.id !== id || !e.purchaseDetails) return e;
          return { ...e, purchaseDetails: { ...e.purchaseDetails, isPriceFetching: false, priceError: 'Price not found for this date — enter manually' } };
        })
      );
    }
  };

  const handleClearDate = (id: string) => {
    setEntries(prev =>
      prev.map(e => {
        if (e.id !== id || !e.purchaseDetails) return e;
        return { ...e, purchaseDetails: { ...e.purchaseDetails, purchasedDate: '', price: '', priceError: undefined } };
      })
    );
  };

  const handleAddAll = async () => {
    const toAdd = entries.filter(e => e.status === 'resolved');
    if (toAdd.length === 0) return;

    // Handle free tier limits: clear purchase details for entries beyond remaining slots
    const remainingSlots = assetLimit - portfolioCount;
    if (tier === 'free') {
      let portfolioSlotsUsed = 0;
      setEntries(prev =>
        prev.map(e => {
          if (e.status !== 'resolved' || !e.purchaseDetails) return e;
          portfolioSlotsUsed++;
          if (portfolioSlotsUsed > remainingSlots) {
            return { ...e, purchaseDetails: undefined, isExpanded: false };
          }
          return e;
        })
      );
    }

    setStep('adding');
    setAddingProgress({ current: 0, total: toAdd.length });

    const addedStocks: Stock[] = [];

    for (let i = 0; i < toAdd.length; i++) {
      const entry = toAdd[i];
      const symbol = entry.resolvedSymbol || entry.symbol;
      const pd = entry.purchaseDetails;
      const hasValidPurchase = pd && pd.shares && parseFloat(pd.shares) > 0;

      setEntries(prev =>
        prev.map(e => e.id === entry.id ? { ...e, status: 'adding' } : e)
      );

      try {
        const data = await fetchStockData(symbol, { includeProfile: true });

        let lots: Stock['lots'] = [];
        let watchlist = true;

        if (hasValidPurchase && pd) {
          let price = parseFloat(pd.price);

          // If price is missing but date is set, try to auto-fill
          if ((!pd.price || isNaN(price)) && pd.purchasedDate) {
            let historicalPrice = findPriceForDate(data.history, pd.purchasedDate);
            if (!historicalPrice) {
              historicalPrice = await fetchHistoricalPrice(symbol, pd.purchasedDate);
            }
            if (historicalPrice) {
              price = historicalPrice;
            } else {
              // Can't resolve price — add as watchlist instead
              lots = [];
              watchlist = true;
              setEntries(prev =>
                prev.map(e =>
                  e.id === entry.id ? { ...e, error: 'Could not find price — added to watchlist' } : e
                )
              );
            }
          }

          if (!isNaN(price) && price > 0) {
            lots = [{
              id: generateId(),
              shares: parseFloat(pd.shares),
              price,
              date: pd.purchasedDate
                ? new Date(pd.purchasedDate + 'T00:00:00').toISOString()
                : new Date().toISOString(),
              currency: pd.currency,
              ...(pd.purchasedDate ? { purchasedDate: pd.purchasedDate } : {}),
            }];
            watchlist = false;
          }
        }

        const newStock: Stock = {
          id: generateId(),
          symbol: symbol.toUpperCase(),
          name: data.name,
          exchange: data.exchange || entry.exchange,
          currency: data.currency,
          currentPrice: data.currentPrice,
          historicalData: data.history,
          groundingUrls: data.groundingUrls,
          marketCap: data.marketCap,
          nextEarningsDate: data.nextEarningsDate,
          instrumentType: data.instrumentType,
          sector: data.sector,
          lastUpdated: new Date().toISOString(),
          userModifiedAt: new Date().toISOString(),
          isLoading: false,
          lots,
          watchlist,
        };

        addedStocks.push(newStock);

        setEntries(prev =>
          prev.map(e => e.id === entry.id ? { ...e, status: 'added' } : e)
        );
      } catch {
        setEntries(prev =>
          prev.map(e =>
            e.id === entry.id
              ? { ...e, status: 'error', error: 'Failed to fetch data' }
              : e
          )
        );
      }

      setAddingProgress({ current: i + 1, total: toAdd.length });
    }

    if (addedStocks.length > 0) {
      onStocksAdded(addedStocks);
    }
  };

  const handleAddSymbol = async () => {
    const symbol = addSymbolInput.trim().toUpperCase();
    if (!symbol) return;

    if (entries.some(e => e.symbol === symbol)) {
      setAddSymbolInput('');
      return;
    }

    const isDuplicate = existingSymbols.includes(symbol);
    const entry: BulkStockEntry = {
      id: generateId(),
      symbol,
      status: isDuplicate ? 'duplicate' : 'resolving',
      error: isDuplicate ? 'Already in portfolio' : undefined,
    };

    setEntries(prev => [...prev, entry]);
    setAddSymbolInput('');

    if (isDuplicate) return;

    setIsAddingSymbol(true);
    try {
      const results = await searchStocks(symbol);
      const exact = results.find(r => r.symbol.toUpperCase() === symbol);
      const best = exact || results[0];

      if (best) {
        setEntries(prev =>
          prev.map(e =>
            e.id === entry.id
              ? { ...e, status: 'resolved', resolvedSymbol: best.symbol, name: best.name, exchange: best.exchange }
              : e
          )
        );
      } else {
        setEntries(prev =>
          prev.map(e =>
            e.id === entry.id ? { ...e, status: 'error', error: 'Symbol not found' } : e
          )
        );
      }
    } catch {
      setEntries(prev =>
        prev.map(e =>
          e.id === entry.id ? { ...e, status: 'error', error: 'Lookup failed' } : e
        )
      );
    }
    setIsAddingSymbol(false);
  };

  const resolvedCount = entries.filter(e => e.status === 'resolved').length;
  const addedCount = entries.filter(e => e.status === 'added').length;
  const errorCount = entries.filter(e => e.status === 'error').length;
  const portfolioEntries = entries.filter(e => e.status === 'resolved' && e.purchaseDetails && e.purchaseDetails.shares);
  const watchlistEntries = entries.filter(e => e.status === 'resolved' && (!e.purchaseDetails || !e.purchaseDetails.shares));
  const allDone = step === 'adding' && addingProgress.current === addingProgress.total;

  // Validation: block submit if any expanded entry has invalid purchase details
  const hasValidationError = entries.some(e => {
    if (e.status !== 'resolved' || !e.purchaseDetails) return false;
    const pd = e.purchaseDetails;
    const shares = parseFloat(pd.shares);
    if (!pd.shares || isNaN(shares) || shares <= 0) return true;
    const price = parseFloat(pd.price);
    if (!pd.purchasedDate && (!pd.price || isNaN(price) || price <= 0)) return true;
    if (pd.price && (!isNaN(price) && price <= 0)) return true;
    return false;
  });

  // Button text
  const getButtonText = () => {
    const pCount = portfolioEntries.length;
    const wCount = watchlistEntries.length;
    if (pCount === 0) return `Add ${resolvedCount} to Watchlist`;
    if (wCount === 0) return `Add ${pCount} to Portfolio`;
    return `Add ${pCount} to Portfolio & ${wCount} to Watchlist`;
  };

  // Free tier warning
  const remainingSlots = assetLimit - portfolioCount;
  const showLimitWarning = tier === 'free' && portfolioEntries.length > remainingSlots && remainingSlots > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Upload">
      {step === 'input' && (
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Enter stock symbols
            </label>
            <textarea
              placeholder={'Paste symbols separated by commas, spaces, or new lines\n\ne.g. AAPL, MSFT, GOOGL, TSLA'}
              className="w-full rounded-xl p-3 text-sm text-[var(--color-text)] outline-none resize-none"
              style={{
                backgroundColor: 'var(--color-bg)',
                border: '1px solid var(--color-input)',
                minHeight: '120px',
              }}
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              autoFocus
            />
            <p className="mt-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {parseSymbols(rawInput).length > 0
                ? `${parseSymbols(rawInput).length} symbol${parseSymbols(rawInput).length !== 1 ? 's' : ''} detected`
                : 'Supports formats: AAPL, MSFT or AAPL MSFT or one per line'}
            </p>
          </div>

          <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-start gap-2">
              <Upload size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                Stocks are added to your <span className="font-medium text-[var(--color-text-secondary)]">watchlist</span> by default.
                You can add purchase details in the next step to add them directly to your portfolio.
              </p>
            </div>
          </div>

          <Button
            onClick={handleResolve}
            className="w-full"
            disabled={parseSymbols(rawInput).length === 0}
          >
            Look Up {parseSymbols(rawInput).length > 0 ? `${parseSymbols(rawInput).length} Symbols` : 'Symbols'}
          </Button>
        </div>
      )}

      {(step === 'review' || step === 'adding') && (
        <div className="space-y-4">
          {/* Progress summary */}
          <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {isResolving && (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span>Looking up symbols...</span>
              </>
            )}
            {!isResolving && step === 'review' && (
              <span>
                {resolvedCount} found
                {errorCount > 0 && ` · ${errorCount} not found`}
              </span>
            )}
            {step === 'adding' && (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span>Adding {addingProgress.current}/{addingProgress.total}...</span>
              </>
            )}
          </div>

          {/* Entries list */}
          <div className="max-h-[400px] space-y-1.5 overflow-y-auto pr-1">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="rounded-lg overflow-hidden"
                style={{
                  backgroundColor: 'var(--color-bg)',
                  border: `1px solid var(--color-border)`,
                  opacity: entry.status === 'duplicate' || entry.status === 'error' ? 0.5 : 1,
                }}
              >
                {/* Main row */}
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {/* Status icon */}
                    {(entry.status === 'pending' || entry.status === 'resolving') && (
                      <Loader2 size={14} className="animate-spin shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                    )}
                    {entry.status === 'resolved' && (
                      <div className="h-3.5 w-3.5 rounded-full shrink-0" style={{ backgroundColor: entry.purchaseDetails ? 'var(--color-accent)' : 'var(--color-surface-hover)', border: entry.purchaseDetails ? undefined : '2px solid var(--color-accent)' }} />
                    )}
                    {entry.status === 'adding' && (
                      <Loader2 size={14} className="animate-spin shrink-0" style={{ color: 'var(--color-accent)' }} />
                    )}
                    {entry.status === 'added' && (
                      <Check size={14} className="shrink-0 text-green-400" />
                    )}
                    {(entry.status === 'error' || entry.status === 'duplicate') && (
                      <AlertCircle size={14} className="shrink-0" style={{ color: entry.status === 'duplicate' ? 'var(--color-text-muted)' : '#f87171' }} />
                    )}

                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm text-[var(--color-text)]">{entry.symbol}</span>
                        {entry.resolvedSymbol && entry.resolvedSymbol !== entry.symbol && (
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>→ {entry.resolvedSymbol}</span>
                        )}
                      </div>
                      {entry.name && (
                        <p className="text-xs truncate" style={{ color: 'var(--color-text-secondary)' }}>{entry.name}</p>
                      )}
                      {entry.error && (
                        <p className="text-xs" style={{ color: entry.status === 'duplicate' ? 'var(--color-text-muted)' : '#f87171' }}>{entry.error}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {entry.exchange && entry.status === 'resolved' && (
                      <span className="rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }}>
                        {entry.exchange}
                      </span>
                    )}
                    {step === 'review' && entry.status !== 'added' && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEntry(entry.id)}
                        className="ml-1 rounded p-0.5 transition-colors hover:bg-[var(--color-surface-hover)]"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Purchase details toggle & form (only for resolved entries in review step) */}
                {entry.status === 'resolved' && step === 'review' && (
                  <div className="px-3 pb-2">
                    {!entry.isExpanded ? (
                      <button
                        type="button"
                        onClick={() => handleTogglePurchaseDetails(entry.id)}
                        className="flex items-center gap-1 text-xs transition-colors hover:underline"
                        style={{ color: 'var(--color-accent)' }}
                      >
                        <Plus size={12} />
                        Add purchase details
                      </button>
                    ) : (
                      <div className="mt-1 space-y-2 rounded-lg p-2.5" style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg) 50%, transparent)', border: '1px solid var(--color-border)' }}>
                        <div className="grid grid-cols-10 gap-2">
                          <div className="col-span-3">
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--color-text-muted)' }}>Shares</label>
                            <input
                              type="number"
                              step="any"
                              placeholder="Qty"
                              className="w-full rounded p-1.5 text-sm text-[var(--color-text)] outline-none"
                              style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-input)' }}
                              value={entry.purchaseDetails?.shares || ''}
                              onChange={(e) => handlePurchaseDetailChange(entry.id, 'shares', e.target.value)}
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--color-text-muted)' }}>
                              {entry.purchaseDetails?.purchasedDate ? 'Price (auto)' : 'Avg Price'}
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                step="any"
                                placeholder={entry.purchaseDetails?.purchasedDate ? 'Auto' : '0.00'}
                                className="w-full rounded p-1.5 text-sm text-[var(--color-text)] outline-none"
                                style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-input)' }}
                                value={entry.purchaseDetails?.price || ''}
                                onChange={(e) => handlePurchaseDetailChange(entry.id, 'price', e.target.value)}
                              />
                              {entry.purchaseDetails?.isPriceFetching && (
                                <Loader2 size={12} className="animate-spin absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
                              )}
                            </div>
                            {entry.purchaseDetails?.priceError && (
                              <p className="text-[10px] mt-0.5" style={{ color: '#f87171' }}>{entry.purchaseDetails.priceError}</p>
                            )}
                          </div>
                          <div className="col-span-3">
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--color-text-muted)' }}>Currency</label>
                            <select
                              value={entry.purchaseDetails?.currency || 'USD'}
                              onChange={(e) => handlePurchaseDetailChange(entry.id, 'currency', e.target.value)}
                              className="w-full rounded p-1.5 text-sm text-[var(--color-text)] outline-none"
                              style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-input)' }}
                            >
                              <option value="USD">USD</option>
                              <option value="CAD">CAD</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] block mb-1" style={{ color: 'var(--color-text-muted)' }}>
                            Purchase Date <span style={{ opacity: 0.5 }}>(optional — auto-fills price)</span>
                          </label>
                          <div className="flex gap-1.5">
                            <input
                              type="date"
                              max={new Date().toISOString().split('T')[0]}
                              className="min-w-0 flex-1 rounded p-1.5 text-sm text-[var(--color-text)] outline-none"
                              style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-input)' }}
                              value={entry.purchaseDetails?.purchasedDate || ''}
                              onChange={(e) => handlePurchaseDetailChange(entry.id, 'purchasedDate', e.target.value)}
                            />
                            {entry.purchaseDetails?.purchasedDate && (
                              <button
                                type="button"
                                onClick={() => handleClearDate(entry.id)}
                                className="rounded px-2 text-xs shrink-0"
                                style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-input)', backgroundColor: 'var(--color-surface-hover)' }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePurchaseDetails(entry.id)}
                          className="text-xs flex items-center gap-1 transition-colors hover:underline"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          Remove details
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add more symbols */}
          {step === 'review' && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add another symbol..."
                className="flex-1 rounded-xl px-3 py-1.5 text-sm text-[var(--color-text)] outline-none"
                style={{
                  backgroundColor: 'var(--color-bg)',
                  border: '1px solid var(--color-input)',
                }}
                value={addSymbolInput}
                onChange={(e) => setAddSymbolInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSymbol();
                  }
                }}
                disabled={isAddingSymbol}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddSymbol}
                disabled={!addSymbolInput.trim() || isAddingSymbol}
              >
                {isAddingSymbol ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              </Button>
            </div>
          )}

          {/* Free tier warning */}
          {showLimitWarning && (
            <p className="text-xs" style={{ color: '#f59e0b' }}>
              You have {remainingSlots} portfolio slot{remainingSlots !== 1 ? 's' : ''} remaining on the free tier.
              Only the first {remainingSlots} stocks with purchase details will be added to your portfolio — the rest will be added to your watchlist instead.
            </p>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            {step === 'review' && (
              <>
                <Button variant="ghost" onClick={() => { setStep('input'); setEntries([]); }} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={handleAddAll}
                  className="flex-1"
                  disabled={resolvedCount === 0 || isResolving || hasValidationError}
                >
                  {getButtonText()}
                </Button>
              </>
            )}
            {step === 'adding' && allDone && (
              <Button onClick={handleClose} className="w-full">
                Done — {addedCount} Added
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
